import React, { useState, useEffect } from 'react';
import { Eye, Users, Store, ExternalLink, Monitor, Smartphone, Tablet, RefreshCw, X, Lock, Globe, LogIn, AlertTriangle, UserCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const AdminPreview = () => {
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [viewportSize, setViewportSize] = useState('desktop');
  const [iframeKey, setIframeKey] = useState(0);
  const [previewMode, setPreviewMode] = useState('public'); // 'public' or 'user-specific'

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [customersRes, vendorsRes] = await Promise.all([
        axiosInstance.get('/admin/customers/').catch(() => ({ data: [] })),
        axiosInstance.get('/vendors/admin/all').catch(() => ({ data: [] }))
      ]);
      setCustomers(customersRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const getViewportDimensions = () => {
    switch (viewportSize) {
      case 'mobile': return { width: 390, height: 844 };
      case 'tablet': return { width: 768, height: 1024 };
      default: return { width: '100%', height: 700 };
    }
  };

  // Define pages with their access level
  const previewPages = {
    public: [
      { url: '/', label: 'Startseite', icon: Globe },
      { url: '/products', label: 'Alle Produkte', icon: Globe },
      { url: '/categories', label: 'Kategorien', icon: Globe },
      { url: '/blog', label: 'Blog', icon: Globe },
      { url: '/contact', label: 'Kontakt', icon: Globe },
      { url: '/become-vendor', label: 'Verkäufer werden', icon: Globe },
      { url: '/account', label: 'Login-Seite', icon: Globe },
    ],
    customer: [
      { url: '/', label: 'Startseite', icon: Globe, public: true },
      { url: '/products', label: 'Produkte', icon: Globe, public: true },
      { url: '/cart', label: 'Warenkorb', icon: Globe, public: true },
      { url: '/wishlist', label: 'Wunschliste', icon: Lock },
      { url: '/profile', label: 'Mein Profil', icon: Lock },
      { url: '/orders', label: 'Meine Bestellungen', icon: Lock },
      { url: '/my-coupons', label: 'Meine Gutscheine', icon: Lock },
    ],
    vendor: [
      { url: '/vendor-dashboard', label: 'Dashboard', icon: Lock },
      { url: '/vendor-dashboard/products', label: 'Meine Produkte', icon: Lock },
      { url: '/vendor-dashboard/orders', label: 'Bestellungen', icon: Lock },
      { url: '/vendor-dashboard/subscription', label: 'Abonnement', icon: Lock },
      { url: '/vendor-dashboard/shop-settings', label: 'Shop-Einstellungen', icon: Lock },
      { url: '/vendor-dashboard/payment-settings', label: 'Zahlungsdaten', icon: Lock },
    ]
  };

  // Handle impersonation - login as user with one click
  const [impersonating, setImpersonating] = useState(false);
  
  const handleImpersonate = async (user, type, redirectTo = '/') => {
    if (impersonating) return;
    
    setImpersonating(true);
    try {
      const response = await axiosInstance.post(`/auth/impersonate/${user.id}`);
      const { token, user: userData, message } = response.data;
      
      // Build the impersonate URL with token and user data
      const userDataEncoded = encodeURIComponent(JSON.stringify(userData));
      const impersonateUrl = `${window.location.origin}/impersonate?token=${token}&user=${userDataEncoded}&redirect=${encodeURIComponent(redirectTo)}`;
      
      toast.success(message || `Als ${userData.first_name} einloggen...`);
      
      // Open in new tab
      window.open(impersonateUrl, '_blank');
      
    } catch (error) {
      console.error('Impersonate error:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Einloggen als Benutzer';
      toast.error(errorMsg);
    } finally {
      setImpersonating(false);
    }
  };

  const dimensions = getViewportDimensions();

  // Open page in new incognito window
  const openInNewWindow = (url, isProtected = false) => {
    const fullUrl = window.location.origin + url;
    if (isProtected) {
      toast.info('Diese Seite erfordert einen Login. Öffnen Sie ein Inkognito-Fenster und melden Sie sich als Benutzer an.', { duration: 5000 });
    }
    window.open(fullUrl, '_blank');
  };

  return (
    <div className="min-h-screen py-4 px-3 sm:px-4" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Eye className="h-6 w-6" />
          Benutzer-Vorschau
        </h1>

        <div className="bg-amber-50 border border-amber-200 p-3 rounded-none mb-4 text-sm">
          <p className="font-medium mb-1">So nutzen Sie die Vorschau:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-700">
            <li><Globe className="h-3 w-3 inline mr-1" /> <strong>Öffentliche Seiten</strong> - Direkt in der Vorschau anzeigen</li>
            <li><Lock className="h-3 w-3 inline mr-1" /> <strong>Geschützte Seiten</strong> - Öffnen Sie ein Inkognito-Fenster und melden Sie sich als Benutzer an</li>
          </ul>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Left Panel - Navigation */}
          <div className="lg:col-span-1 space-y-4">
            <Tabs defaultValue="public" className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-[#C4B5A4] p-1 rounded-none border-0 gap-1">
                <TabsTrigger value="public" className="flex items-center gap-1 text-xs px-1 rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 py-2">
                  <Globe className="h-3 w-3" />
                  Öffentlich
                </TabsTrigger>
                <TabsTrigger value="customers" className="flex items-center gap-1 text-xs px-1 rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 py-2">
                  <Users className="h-3 w-3" />
                  Kunden
                </TabsTrigger>
                <TabsTrigger value="vendors" className="flex items-center gap-1 text-xs px-1 rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 py-2">
                  <Store className="h-3 w-3" />
                  Verkäufer
                </TabsTrigger>
              </TabsList>

              {/* PUBLIC PAGES TAB */}
              <TabsContent value="public">
                <Card className="rounded-none">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Globe className="h-4 w-4 text-green-600" />
                      Öffentliche Seiten
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xs text-gray-500 mb-2">Diese Seiten sind für alle Besucher sichtbar:</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {previewPages.public.map((page) => (
                        <Button
                          key={page.url}
                          variant="outline"
                          size="sm"
                          className="text-xs rounded-none h-9 justify-start"
                          onClick={() => {
                            setPreviewUrl(page.url);
                            setPreviewMode('public');
                            setSelectedUser({ type: 'public', name: 'Besucher' });
                          }}
                        >
                          <Globe className="h-3 w-3 mr-1 text-green-600" />
                          {page.label}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* CUSTOMERS TAB */}
              <TabsContent value="customers">
                <Card className="rounded-none">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-600" />
                      Kunden-Ansicht
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Quick Preview Links */}
                    <div className="border-b pb-3">
                      <p className="text-xs text-gray-500 mb-2">Kunden-Seiten:</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {previewPages.customer.map((page) => {
                          const isPublic = page.public;
                          const PageIcon = page.icon;
                          return (
                            <Button
                              key={page.url}
                              variant="outline"
                              size="sm"
                              className="text-xs rounded-none h-9 justify-start"
                              onClick={() => {
                                if (isPublic) {
                                  setPreviewUrl(page.url);
                                  setPreviewMode('public');
                                  setSelectedUser({ type: 'customer', name: 'Kunden-Ansicht' });
                                } else {
                                  openInNewWindow(page.url, true);
                                }
                              }}
                            >
                              <PageIcon className={`h-3 w-3 mr-1 ${isPublic ? 'text-green-600' : 'text-amber-600'}`} />
                              {page.label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Customer List */}
                    <div>
                      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                        <UserCircle className="h-3 w-3" />
                        Registrierte Kunden ({customers.length}):
                      </p>
                      {loading ? (
                        <p className="text-sm text-gray-500">Lädt...</p>
                      ) : customers.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Keine Kunden registriert</p>
                      ) : (
                        <div className="max-h-48 overflow-y-auto space-y-1.5">
                          {customers.slice(0, 20).map((customer) => (
                            <div
                              key={customer.id}
                              className="flex items-center justify-between p-2 text-sm border rounded-none hover:bg-gray-50"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">
                                  {customer.first_name} {customer.last_name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">{customer.email}</p>
                              </div>
                              <Button
                                size="sm"
                                variant="default"
                                className="rounded-none h-7 text-xs ml-2 bg-blue-600 hover:bg-blue-700"
                                onClick={() => handleImpersonate(customer, 'customer', '/profile')}
                                disabled={impersonating}
                              >
                                <LogIn className="h-3 w-3 mr-1" />
                                Einloggen
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* VENDORS TAB */}
              <TabsContent value="vendors">
                <Card className="rounded-none">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Store className="h-4 w-4 text-purple-600" />
                      Verkäufer-Ansicht
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Quick Preview Links */}
                    <div className="border-b pb-3">
                      <p className="text-xs text-gray-500 mb-2">Verkäufer-Dashboard Seiten:</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {previewPages.vendor.map((page) => {
                          const PageIcon = page.icon;
                          return (
                            <Button
                              key={page.url}
                              variant="outline"
                              size="sm"
                              className="text-xs rounded-none h-9 justify-start"
                              onClick={() => openInNewWindow(page.url, true)}
                            >
                              <PageIcon className="h-3 w-3 mr-1 text-amber-600" />
                              {page.label}
                            </Button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-amber-700 mt-2 bg-amber-50 p-2 rounded-none">
                        <AlertTriangle className="h-3 w-3 inline mr-1" />
                        Diese Seiten erfordern einen Verkäufer-Login.
                      </p>
                    </div>

                    {/* Vendor List */}
                    <div>
                      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                        <Store className="h-3 w-3" />
                        Verkäufer-Shops ({vendors.filter(v => v.status === 'approved').length} aktiv):
                      </p>
                      {loading ? (
                        <p className="text-sm text-gray-500">Lädt...</p>
                      ) : vendors.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Keine Verkäufer registriert</p>
                      ) : (
                        <div className="max-h-48 overflow-y-auto space-y-1.5">
                          {vendors.slice(0, 20).map((vendor) => (
                            <div
                              key={vendor.id}
                              className="p-2 text-sm border rounded-none hover:bg-gray-50"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-medium truncate">
                                  {vendor.vendor_profile?.business_name || `${vendor.first_name} ${vendor.last_name}`}
                                </p>
                                <span className={`text-xs px-1.5 py-0.5 ${
                                  vendor.vendor_profile?.subscription_tier === 'gold' 
                                    ? 'bg-amber-100 text-amber-700' 
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {vendor.vendor_profile?.subscription_tier === 'gold' ? 'Gold' : 'Basis'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-gray-500 truncate">{vendor.email}</p>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-none h-6 text-xs"
                                    onClick={() => {
                                      setPreviewUrl(`/shop/${vendor.id}`);
                                      setPreviewMode('public');
                                      setSelectedUser({ type: 'vendor-shop', name: vendor.vendor_profile?.business_name || 'Shop' });
                                    }}
                                  >
                                    Shop
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="rounded-none h-6 text-xs bg-purple-600 hover:bg-purple-700"
                                    onClick={() => handleImpersonate(vendor, 'vendor', '/vendor-dashboard')}
                                    disabled={impersonating}
                                    title="Als Verkäufer einloggen"
                                  >
                                    <LogIn className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel - Preview */}
          <div className="lg:col-span-2">
            <Card className="rounded-none h-full">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Live-Vorschau
                    {selectedUser && (
                      <span className="text-xs font-normal text-gray-500 ml-1">
                        — {selectedUser.name}
                      </span>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {/* Viewport Buttons */}
                    <div className="flex border rounded-none">
                      <button
                        onClick={() => setViewportSize('mobile')}
                        className={`p-1.5 ${viewportSize === 'mobile' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
                        title="Mobile (390×844)"
                      >
                        <Smartphone className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setViewportSize('tablet')}
                        className={`p-1.5 border-x ${viewportSize === 'tablet' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
                        title="Tablet (768×1024)"
                      >
                        <Tablet className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setViewportSize('desktop')}
                        className={`p-1.5 ${viewportSize === 'desktop' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
                        title="Desktop"
                      >
                        <Monitor className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {/* Refresh & Open in new tab */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none h-8"
                      onClick={() => setIframeKey(prev => prev + 1)}
                      title="Neu laden"
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                    {previewUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none h-8"
                        onClick={() => window.open(previewUrl, '_blank')}
                        title="In neuem Tab öffnen"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!previewUrl ? (
                  <div className="flex items-center justify-center h-96 bg-gray-100 text-gray-500">
                    <div className="text-center p-6">
                      <Eye className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium mb-2">Vorschau starten</p>
                      <p className="text-sm">Wählen Sie links eine Seite aus, um sie hier anzuzeigen.</p>
                      <div className="mt-4 text-xs text-gray-400">
                        <p>Tipp: Öffentliche Seiten werden direkt angezeigt.</p>
                        <p>Geschützte Seiten erfordern einen separaten Login.</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="border bg-white overflow-hidden mx-auto shadow-lg"
                    style={{
                      width: typeof dimensions.width === 'number' ? dimensions.width : dimensions.width,
                      maxWidth: '100%'
                    }}
                  >
                    {/* Browser Chrome */}
                    <div className="bg-gray-800 text-white text-xs px-2 py-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="flex gap-1">
                          <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                          <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                        </div>
                        <div className="bg-gray-700 rounded px-2 py-0.5 flex-1 truncate text-gray-300 max-w-xs">
                          {window.location.origin}{previewUrl}
                        </div>
                      </div>
                      <button 
                        onClick={() => { setPreviewUrl(''); setSelectedUser(null); }}
                        className="hover:bg-gray-700 p-1 rounded ml-2"
                        title="Schließen"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    {/* Iframe */}
                    <iframe
                      key={iframeKey}
                      src={previewUrl}
                      className="w-full border-0"
                      style={{ 
                        height: typeof dimensions.height === 'number' ? dimensions.height : 700
                      }}
                      title="Preview"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="bg-white p-3 rounded-none border">
            <h3 className="font-medium text-sm mb-2 flex items-center gap-2">
              <Globe className="h-4 w-4 text-green-600" />
              Öffentliche Vorschau
            </h3>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Startseite, Produkte, Kategorien, Blog</li>
              <li>• Kontaktseite und Rechtliches</li>
              <li>• Verkäufer-Shop-Seiten</li>
              <li>• Warenkorb (ohne Checkout)</li>
            </ul>
          </div>
          <div className="bg-white p-3 rounded-none border border-blue-200 bg-blue-50">
            <h3 className="font-medium text-sm mb-2 flex items-center gap-2">
              <LogIn className="h-4 w-4 text-blue-600" />
              Als Benutzer einloggen
            </h3>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Klicken Sie auf <strong>"Einloggen"</strong> bei einem Kunden/Verkäufer</li>
              <li>• Ein neuer Tab öffnet sich automatisch</li>
              <li>• Sie sind sofort als dieser Benutzer eingeloggt</li>
              <li>• Session läuft nach 1 Stunde ab</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPreview;
